import { useEffect } from 'react'
import * as Sentry from '@sentry/react'
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  Routes,
  useLocation,
  useNavigationType,
} from 'react-router-dom'
import { firebase } from '../firebase'

export const initSentry = ({
  dsn,
  release,
  environment,
  graphqlEndpoint,
}: {
  dsn: string
  environment: string
  release: string
  graphqlEndpoint: string
}) => {
  Sentry.init({
    dsn,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.graphqlClientIntegration({
        endpoints: [graphqlEndpoint],
      }),
      Sentry.replayIntegration(),
    ],
    environment,
    release,

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.75,

    sendDefaultPii: true,

    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.1,
    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 1.0,
  })
}

export const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)
export const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)

export const reportError =
  (isLocalRun: boolean) =>
  (e: unknown, level: Sentry.SeverityLevel = 'error', extra?: Record<string, unknown>) => {
    if (isLocalRun) {
      // eslint-disable-next-line no-console
      console.error(e, level, extra)
    } else {
      const firebaseUser = firebase.getCurrentUser()
      const claims = firebaseUser ? firebase.getXHasuraClaims() : {}

      const user = firebaseUser ? { id: firebaseUser.uid } : undefined
      Sentry.withScope(scope => {
        scope.setTags(claims)
        Sentry.captureException(e, { extra, level, user })
      })
    }
  }
